import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface CacheItem {
  data: any
  timestamp: number
  ttl: number // Time to live em milliseconds
}

export const useCacheStore = defineStore('cache', () => {
  const cache = ref<Record<string, CacheItem>>({})
  const maxSize = ref(100) // Máximo de itens no cache
  const defaultTTL = ref(5 * 60 * 1000) // 5 minutos

  // Computed para estatísticas do cache
  const cacheStats = computed(() => {
    const items = Object.keys(cache.value)
    const expired = items.filter(key => isExpired(key))
    const totalSize = JSON.stringify(cache.value).length

    return {
      totalItems: items.length,
      expiredItems: expired.length,
      validItems: items.length - expired.length,
      totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
      hitRate: 0 // Será calculado com métricas reais
    }
  })

  // Verificar se item está expirado
  const isExpired = (key: string): boolean => {
    const item = cache.value[key]
    if (!item) return true
    
    const now = Date.now()
    return now - item.timestamp > item.ttl
  }

  // Limpar itens expirados
  const clearExpired = () => {
    Object.keys(cache.value).forEach(key => {
      if (isExpired(key)) {
        delete cache.value[key]
      }
    })
  }

  // Adicionar item ao cache
  const set = (key: string, data: any, ttl?: number) => {
    // Limpar cache se estiver muito grande
    if (Object.keys(cache.value).length >= maxSize.value) {
      clearExpired()
      
      // Se ainda estiver cheio, remover item mais antigo
      if (Object.keys(cache.value).length >= maxSize.value) {
        const oldestKey = Object.keys(cache.value).reduce((oldest, current) => {
          return cache.value[current].timestamp < cache.value[oldest].timestamp ? current : oldest
        })
        delete cache.value[oldestKey]
      }
    }

    cache.value[key] = {
      data,
      timestamp: Date.now(),
      ttl: ttl || defaultTTL.value
    }
  }

  // Obter item do cache
  const get = (key: string) => {
    const item = cache.value[key]
    
    if (!item) return null
    
    if (isExpired(key)) {
      delete cache.value[key]
      return null
    }

    return item.data
  }

  // Verificar se item existe e não está expirado
  const has = (key: string): boolean => {
    return get(key) !== null
  }

  // Remover item específico
  const remove = (key: string) => {
    delete cache.value[key]
  }

  // Limpar todo o cache
  const clear = () => {
    cache.value = {}
  }

  // Obter ou definir com fallback
  const getOrSet = async (key: string, fetcher: () => Promise<any>, ttl?: number) => {
    const cached = get(key)
    if (cached !== null) {
      return cached
    }

    const data = await fetcher()
    set(key, data, ttl)
    return data
  }

  // Cache com invalidação por tags
  const setWithTags = (key: string, data: any, tags: string[], ttl?: number) => {
    set(key, { ...data, _tags: tags }, ttl)
  }

  const invalidateByTag = (tag: string) => {
    Object.keys(cache.value).forEach(key => {
      const item = cache.value[key]
      if (item.data._tags && item.data._tags.includes(tag)) {
        delete cache.value[key]
      }
    })
  }

  return {
    cache,
    maxSize,
    defaultTTL,
    cacheStats,
    set,
    get,
    has,
    remove,
    clear,
    clearExpired,
    getOrSet,
    setWithTags,
    invalidateByTag
  }
}, {
  persist: {
    key: 'cache-store',
    storage: localStorage,
    paths: ['cache'],
    // Não persistir itens expirados
    beforeRestore: (context) => {
      const now = Date.now()
      Object.keys(context.store.cache).forEach(key => {
        const item = context.store.cache[key]
        if (now - item.timestamp > item.ttl) {
          delete context.store.cache[key]
        }
      })
    }
  }
})
